// Credit Bureau Data Transformation Utilities

export interface CreditBureauRawResponse {
  [key: string]: any;
}

export interface PersonalInfo {
  name?: string;
  pan?: string;
  gender?: string;
  mobile?: string;
  dob?: string;
  credit_score?: string | number;
  [key: string]: any;
}

export interface CreditFacility {
  "Loan Type"?: string;
  "Bank"?: string;
  "Status"?: string;
  "Loan Sanctioned (INR)"?: string | number;
  "Current Balance (INR)"?: string | number;
  "EMI Amount (INR)"?: string | number;
  "Tenure (Months)"?: string | number;
  [key: string]: any;
}

export interface EmiObligation {
  "Loan Type"?: string;
  "Bank"?: string;
  "Outstanding (INR)"?: number;
  "Estimated EMI (INR)"?: number;
  [key: string]: any;
}

export interface DpdStatusItem {
  "Loan Type"?: string;
  "Last Payment Date"?: string;
  "EMI Amount"?: string | number;
  "DPD Status"?: string;
  "SMA Status"?: string;
  [key: string]: any;
}

export interface EnhancedCreditBureauData {
  score: number;
  maxScore: number;
  grade: string;
  riskFlags: string[];
  advisoryTips: string[];
  reportId: string;
  lastUpdated: string;
  personalInfo: PersonalInfo;
  creditFacilitiesOverview: CreditFacility[];
  estimatedEmiObligations: EmiObligation[];
  totalEmiAmount: number;
  enquiryDetails: any[];
  securedLoans: any[];
  closedLoanAccounts: any[];
  loanAppraisalScorecard: any[];
  finalScore: number;
  finalGrade: string;
  eligibility: string;
  dpdStatusAnalysis: DpdStatusItem[];
  observationsAndFinalNotes: string[];
}

// Credit Score Grading
export const getGrade = (score: number): string => {
  if (score >= 750) return 'Excellent';
  if (score >= 700) return 'Good';
  if (score >= 650) return 'Fair';
  if (score >= 600) return 'Poor';
  return 'Very Poor';
};

// Get grade color for UI
export const getGradeColor = (grade: string): string => {
  switch (grade) {
    case 'Excellent': return 'text-green-600 bg-green-50';
    case 'Good': return 'text-blue-600 bg-blue-50';
    case 'Fair': return 'text-yellow-600 bg-yellow-50';
    case 'Poor': return 'text-orange-600 bg-orange-50';
    case 'Very Poor': return 'text-red-600 bg-red-50';
    default: return 'text-gray-600 bg-gray-50';
  }
};

// Calculate final score based on various factors
export const calculateFinalScore = (
  creditScore: number,
  activeLoans: number,
  recentEnquiries: number,
  dpdStatus: any[]
): number => {
  let finalScore = creditScore;
  
  // Penalty for multiple active loans
  if (activeLoans > 5) finalScore -= 20;
  else if (activeLoans > 3) finalScore -= 10;
  
  // Penalty for high enquiry frequency
  if (recentEnquiries > 10) finalScore -= 30;
  else if (recentEnquiries > 5) finalScore -= 15;
  
  // Penalty for DPD (Days Past Due) issues
  const dpdIssues = dpdStatus.filter(item => 
    item.status && item.status.toLowerCase().includes('overdue')
  );
  finalScore -= dpdIssues.length * 25;
  
  return Math.max(300, Math.min(850, finalScore));
};

// Generate risk flags based on data analysis
export const generateRiskFlags = (
  creditScore: number,
  recentEnquiries: any[],
  activeLoans: CreditFacility[],
  dpdStatus: any[]
): string[] => {
  const riskFlags: string[] = [];
  
  // High enquiry frequency check
  if (recentEnquiries.length > 10) {
    riskFlags.push(`🚨 High enquiry frequency: ${recentEnquiries.length} enquiries in past 6 months`);
  } else if (recentEnquiries.length > 5) {
    riskFlags.push(`⚠️ Moderate enquiry frequency: ${recentEnquiries.length} enquiries in past 6 months`);
  }
  
  // Multiple active loans check
  if (activeLoans.length > 5) {
    riskFlags.push(`🚨 Multiple active credit facilities: ${activeLoans.length} active accounts`);
  } else if (activeLoans.length > 3) {
    riskFlags.push(`⚠️ Several active credit facilities: ${activeLoans.length} active accounts`);
  }
  
  // Credit score check
  if (creditScore < 600) {
    riskFlags.push('🚨 Credit score significantly below recommended threshold');
  } else if (creditScore < 650) {
    riskFlags.push('⚠️ Credit score below recommended threshold');
  }
  
  // DPD status check
  const dpdIssues = dpdStatus.filter(item => 
    item.status && item.status.toLowerCase().includes('overdue')
  );
  if (dpdIssues.length > 0) {
    riskFlags.push(`🚨 Payment delays detected: ${dpdIssues.length} accounts with overdue payments`);
  }
  
  // High EMI burden check
  const totalEmi = activeLoans.reduce((sum, loan) => {
    const emi = parseFloat(String(loan["EMI Amount (INR)"] || 0));
    return sum + (isNaN(emi) ? 0 : emi);
  }, 0);
  
  if (totalEmi > 50000) {
    riskFlags.push(`⚠️ High EMI burden: ₹${totalEmi.toLocaleString()} monthly obligations`);
  }
  
  return riskFlags;
};

// Generate advisory tips based on analysis
export const generateAdvisoryTips = (
  creditScore: number,
  recentEnquiries: any[],
  activeLoans: CreditFacility[],
  riskFlags: string[]
): string[] => {
  const advisoryTips: string[] = [];
  
  if (recentEnquiries.length > 5) {
    advisoryTips.push('📉 Reduce credit enquiries - avoid applying for new credit for 6-12 months to improve your credit profile');
  }
  
  if (activeLoans.length > 3) {
    advisoryTips.push('🔄 Consider consolidating multiple loans to improve credit utilization and reduce EMI burden');
  }
  
  if (creditScore < 700) {
    advisoryTips.push('📈 Focus on timely repayments to improve credit score over the next 6-12 months');
    advisoryTips.push('💳 Keep credit utilization below 30% of available limits on credit cards');
  }
  
  if (creditScore < 650) {
    advisoryTips.push('🎯 Consider secured credit cards or FD-backed loans to rebuild credit history');
    advisoryTips.push('📊 Set up automatic payments to avoid future delays and improve payment history');
  }
  
  if (riskFlags.some(flag => flag.includes('EMI burden'))) {
    advisoryTips.push('💰 Review monthly budget and consider increasing income or reducing discretionary expenses');
  }
  
  if (riskFlags.some(flag => flag.includes('overdue'))) {
    advisoryTips.push('⏰ Clear all overdue amounts immediately to prevent further credit score damage');
  }
  
  // General tips
  advisoryTips.push('📱 Monitor your credit report quarterly to track improvements and catch errors early');
  advisoryTips.push('🏦 Maintain a mix of secured and unsecured credit to demonstrate responsible credit management');
  
  return advisoryTips;
};

// Get eligibility status based on score
export const getEligibility = (finalScore: number, creditScore: number): string => {
  if (finalScore >= 750 && creditScore >= 750) {
    return 'Excellent - Eligible for premium credit products with best rates';
  } else if (finalScore >= 700 && creditScore >= 700) {
    return 'Good - Eligible for most credit products with competitive rates';
  } else if (finalScore >= 650 && creditScore >= 650) {
    return 'Fair - Eligible for standard credit products, may require higher interest rates';
  } else if (finalScore >= 600 && creditScore >= 600) {
    return 'Limited - Eligible for secured credit products and some unsecured loans';
  } else {
    return 'Poor - May need to rebuild credit history before accessing most credit products';
  }
};

// Enhanced scorecard with weights and remarks
export const enhanceScorecard = (scorecard: any[]): any[] => {
  return scorecard.map((item: any) => ({
    ...item,
    Weight: generateScorecardWeight(item.Metric),
    Remarks: generateScorecardRemarks(item.Metric, item.Value)
  }));
};

const generateScorecardWeight = (metric: string): string => {
  const weights: Record<string, string> = {
    'Payment History': '35%',
    'Credit Utilization': '30%',
    'Length of Credit History': '15%',
    'Credit Mix': '10%',
    'New Credit': '10%',
    'Total Accounts': '5%',
    'Age of Oldest Account': '5%'
  };
  
  return weights[metric] || '5%';
};

const generateScorecardRemarks = (metric: string, value: any): string => {
  const valueStr = String(value).toLowerCase();
  
  if (metric.includes('Payment')) {
    if (valueStr.includes('excellent') || valueStr.includes('100%')) {
      return 'Excellent payment track record';
    } else if (valueStr.includes('good') || valueStr.includes('90%')) {
      return 'Good payment consistency with minor delays';
    } else {
      return 'Needs improvement in payment timeliness';
    }
  } else if (metric.includes('Utilization')) {
    const utilization = parseFloat(valueStr);
    if (utilization < 30) {
      return 'Optimal credit utilization';
    } else if (utilization < 50) {
      return 'Moderate utilization - room for improvement';
    } else {
      return 'High utilization - consider reducing balances';
    }
  } else if (metric.includes('History') || metric.includes('Age')) {
    return 'Contributes to credit stability and trustworthiness';
  } else if (metric.includes('Mix')) {
    return 'Demonstrates ability to manage different types of credit';
  }
  
  return 'Factor in overall credit assessment';
};

// Format currency for display
export const formatCurrency = (amount: string | number | undefined): string => {
  const num = parseFloat(String(amount || 0));
  if (isNaN(num)) return '₹0';
  return `₹${num.toLocaleString('en-IN')}`;
};

// Format date for display
export const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-IN');
  } catch {
    return dateString;
  }
};

// Estimate EMI based on outstanding balance and loan type
export const estimateEMI = (outstandingBalance: number, loanType: string): number => {
  console.log(`=== ESTIMATE EMI CALLED ===`);
  console.log(`Input: outstanding=${outstandingBalance}, loanType="${loanType}"`);
  
  if (outstandingBalance <= 0) {
    console.log(`Returning 0 - outstanding balance is ${outstandingBalance}`);
    return 0;
  }
  
  // Rough EMI estimation based on loan type (these are approximate percentages of outstanding balance)
  const emiRates: Record<string, number> = {
    'home loan': 0.008,      // ~0.8% of outstanding (120-240 months typical)
    'personal loan': 0.04,   // ~4% of outstanding (24-60 months typical) 
    'car loan': 0.025,       // ~2.5% of outstanding (36-84 months typical)
    'credit card': 0.05,     // ~5% of outstanding (minimum payment)
    'education loan': 0.015, // ~1.5% of outstanding (120-180 months typical)
    'business loan': 0.03,   // ~3% of outstanding (36-120 months typical)
    'default': 0.025         // ~2.5% default rate
  };
  
  const loanTypeKey = loanType.toLowerCase();
  console.log(`Loan type key: "${loanTypeKey}"`);
  
  const matchingKey = Object.keys(emiRates).find(key => loanTypeKey.includes(key));
  const rate = matchingKey ? emiRates[matchingKey] : emiRates.default;
  
  console.log(`Matching key: "${matchingKey}", Rate: ${rate}`);
  
  const estimatedEMI = Math.round(outstandingBalance * rate);
  console.log(`Calculated EMI: ${outstandingBalance} * ${rate} = ${estimatedEMI}`);
  
  return estimatedEMI;
};

// Generate detailed EMI obligations from credit facilities
export const generateEmiObligations = (creditFacilities: CreditFacility[]): EmiObligation[] => {
  console.log('generateEmiObligations input:', creditFacilities);
  
  // Log each facility's raw data first
  creditFacilities.forEach((facility, index) => {
    console.log(`Raw Facility ${index}:`, facility);
    console.log(`Keys in facility:`, Object.keys(facility));
  });
  
  const filtered = creditFacilities.filter(facility => {
    // Check multiple field names for outstanding balance
    const outstandingValue = facility["Current Balance (INR)"] || 
                           facility["Outstanding (INR)"] || 
                           facility["Current Balance"] || 
                           facility["Outstanding"] ||
                           facility["Loan Sanctioned (INR)"];  // Use sanctioned amount as fallback
    
    const hasOutstanding = outstandingValue && parseFloat(String(outstandingValue)) > 0;
    
    // Check multiple field names for EMI amount
    const emiValue = facility["EMI Amount (INR)"] || 
                    facility["EMI"] || 
                    facility["Monthly EMI"] || 
                    facility["EMI Amount"] ||
                    facility["Monthly Payment"];
    const hasEmi = emiValue && parseFloat(String(emiValue)) > 0;
    
    console.log(`Facility: ${facility["Loan Type"]} - Outstanding: ${outstandingValue} (${hasOutstanding}) - EMI: ${emiValue} (${hasEmi})`);
    
    // Include facility if it has outstanding balance OR if it's an active loan with sanctioned amount
    return hasOutstanding || (facility["Status"] === 'Active' && facility["Loan Sanctioned (INR)"] && parseFloat(String(facility["Loan Sanctioned (INR)"])) > 0);
  });
  
  console.log('Filtered facilities:', filtered);
  
  const result = filtered.map(facility => {
    // Try multiple field names for outstanding balance
    console.log(`=== PROCESSING FACILITY ===`);
    console.log(`Current Balance (INR):`, facility["Current Balance (INR)"]);
    console.log(`Outstanding (INR):`, facility["Outstanding (INR)"]);
    console.log(`Current Balance:`, facility["Current Balance"]);
    console.log(`Outstanding:`, facility["Outstanding"]);
    
    const outstandingValue = facility["Current Balance (INR)"] || 
                           facility["Outstanding (INR)"] || 
                           facility["Current Balance"] || 
                           facility["Outstanding"] || 
                           facility["Loan Sanctioned (INR)"] ||  // Use sanctioned amount as fallback
                           0;
    
    console.log(`Selected outstanding value:`, outstandingValue);
    
    const outstanding = parseFloat(String(outstandingValue)) || 0;
    console.log(`Parsed outstanding:`, outstanding);
    
    // If we're using sanctioned amount, note it's an estimate
    const isEstimatedFromSanctioned = !facility["Current Balance (INR)"] && 
                                     !facility["Outstanding (INR)"] && 
                                     !facility["Current Balance"] && 
                                     !facility["Outstanding"] && 
                                     facility["Loan Sanctioned (INR)"];
    
    if (isEstimatedFromSanctioned) {
      console.log(`Using sanctioned amount as outstanding balance estimate for ${facility["Loan Type"]}`);
    }
    
    // Try multiple field names for EMI amount
    let emi = parseFloat(String(
      facility["EMI Amount (INR)"] || 
      facility["EMI"] || 
      facility["Monthly EMI"] || 
      facility["EMI Amount"] ||
      facility["Monthly Payment"] ||
      0
    )) || 0;
    
    // If no EMI found and we have outstanding balance, estimate it
    if (emi === 0 && outstanding > 0) {
      emi = estimateEMI(outstanding, facility["Loan Type"] || 'default');
      console.log(`Estimated EMI for ${facility["Loan Type"]}: ${emi} (based on outstanding: ${outstanding})`);
    }
    
    console.log(`Mapping facility: ${facility["Loan Type"]} - Outstanding: ${outstanding} - EMI: ${emi}`);
    console.log(`All facility values:`, facility);
    
    return {
      "Loan Type": facility["Loan Type"] || 'Unknown',
      "Bank": facility["Bank"] || 'Unknown',
      "Outstanding (INR)": outstanding,
      "Estimated EMI (INR)": emi
    };
  });
  
  console.log('Final EMI obligations result:', result);
  return result;
};

// Enhanced DPD status processing
export const enhanceDpdAnalysis = (dpdStatus: any[]): DpdStatusItem[] => {
  return dpdStatus.map(item => ({
    "Loan Type": item["Loan Type"] || item.loanType || 'Unknown',
    "Last Payment Date": item["Last Payment Date"] || item.lastPaymentDate || 'N/A',
    "EMI Amount": item["EMI Amount"] || item.emiAmount || 0,
    "DPD Status": item["DPD Status"] || item.dpdStatus || '0',
    "SMA Status": item["SMA Status"] || item.smaStatus || 'Standard'
  }));
};

// Transform raw API response to enhanced data
export const transformCreditBureauData = (apiResponse: any): EnhancedCreditBureauData => {
  // Handle both successful responses and error responses with partial data
  let responseData = apiResponse;
  
  // If it's an error response with details.data, use that
  if (apiResponse.details && apiResponse.details.data) {
    responseData = [{ "Personal Information": apiResponse.details.data }];
  }
  
  // If response is not an array, try to extract meaningful data
  if (!Array.isArray(responseData)) {
    if (responseData.data) {
      responseData = [{ "Personal Information": responseData.data }];
    } else {
      responseData = [{ "Personal Information": responseData }];
    }
  }
  
  // Extract data from array indices
  const personalInfo = responseData[0]?.["Personal Information"] || {};
  const creditFacilities = responseData[1]?.["Credit Facilities Overview"] || [];
  const enquiries = responseData[2]?.["Enquiry Details (Past 6 Months)"] || [];
  const securedLoans = responseData[3]?.["Secured Loans & Collateral Estimate"] || [];
  const closedAccounts = responseData[4]?.["Closed Loan Accounts"] || [];
  const scorecard = responseData[5]?.["Loan Appraisal Scorecard"] || [];
  const dpdStatus = responseData[6]?.["DPD Status Analysis"] || [];
  
  const creditScore = parseInt(String(personalInfo?.credit_score || 0)) || 0;
  const activeLoans = Array.isArray(creditFacilities) ? creditFacilities : [];
  const recentEnquiries = Array.isArray(enquiries) ? enquiries : [];
  
  // Calculate enhanced metrics
  const finalScore = calculateFinalScore(creditScore, activeLoans.length, recentEnquiries.length, dpdStatus);
  const grade = getGrade(creditScore);
  const finalGrade = getGrade(finalScore);
  const riskFlags = generateRiskFlags(creditScore, recentEnquiries, activeLoans, dpdStatus);
  const advisoryTips = generateAdvisoryTips(creditScore, recentEnquiries, activeLoans, riskFlags);
  const eligibility = getEligibility(finalScore, creditScore);
  
  // Generate detailed EMI obligations
  console.log('=== CREDIT BUREAU UTILS DEBUG ===');
  console.log('Active loans received:', activeLoans);
  const estimatedEmiObligations = generateEmiObligations(activeLoans);
  console.log('Generated EMI obligations:', estimatedEmiObligations);
  
  // Calculate total EMI amount
  const totalEmiAmount = estimatedEmiObligations.reduce((sum, emi) => {
    return sum + (emi["Estimated EMI (INR)"] || 0);
  }, 0);
  console.log('Total EMI amount calculated:', totalEmiAmount);
  
  // Enhanced DPD analysis
  const enhancedDpdAnalysis = enhanceDpdAnalysis(Array.isArray(dpdStatus) ? dpdStatus : []);
  
  // Enhanced scorecard
  const enhancedScorecard = enhanceScorecard(Array.isArray(scorecard) ? scorecard : []);
  
  // Generate observations and final notes
  const observationsAndFinalNotes = [
    `Credit assessment completed for ${personalInfo.name || 'Unknown'}`,
    `Overall credit health: ${finalGrade} (Score: ${finalScore}/850)`,
    `Current credit obligations: ${formatCurrency(totalEmiAmount)} monthly EMI`,
    `Active credit facilities: ${activeLoans.length} accounts`,
    `DPD analysis: ${enhancedDpdAnalysis.length} accounts reviewed`,
    `Recommendation: ${eligibility}`,
    ...riskFlags.map(flag => `Risk Factor: ${flag}`),
    ...advisoryTips.slice(0, 3).map(tip => `Advisory: ${tip}`)
  ];
  
  return {
    score: creditScore,
    maxScore: 850,
    grade,
    riskFlags,
    advisoryTips,
    reportId: `CR-${personalInfo.pan || Date.now()}`,
    lastUpdated: new Date().toISOString(),
    personalInfo,
    creditFacilitiesOverview: activeLoans,
    estimatedEmiObligations,
    totalEmiAmount,
    enquiryDetails: recentEnquiries,
    securedLoans: Array.isArray(securedLoans) ? securedLoans : [],
    closedLoanAccounts: Array.isArray(closedAccounts) ? closedAccounts : [],
    loanAppraisalScorecard: enhancedScorecard,
    finalScore: Math.round(finalScore * 100) / 100,
    finalGrade,
    eligibility,
    dpdStatusAnalysis: enhancedDpdAnalysis,
    observationsAndFinalNotes
  };
};
